import React, { useState, useCallback } from 'react';
import { 
  DISTRICT_NAME, 
  RECIPIENTS, 
  TRACKING_EMAIL,
  BOARD_MEMBERS, 
  EMAIL_TEMPLATES, 
  CALL_SCRIPTS,
  IG_CAPTION_TEMPLATE,
  UI_CONFIG,
  ACTION_CARDS,
  NEXT_MEETING,
  SOCIAL_CONFIG
} from './constants.ts';

// --- Components ---

const Toast = ({ message, visible }: { message: string, visible: boolean }) => (
  <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ease-out ${visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95 pointer-events-none'}`}>
    <div className="bg-slate-900/95 backdrop-blur-md text-white px-6 md:px-8 py-3 md:py-4 rounded-2xl md:rounded-3xl shadow-2xl flex items-center gap-3 font-bold text-xs md:text-sm whitespace-nowrap">
      <div className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse shrink-0"></div>
      {message}
    </div>
  </div>
);

const SectionHeader = ({ title, id, number }: { title: string, id: string, number: string }) => (
  <div id={id} className="flex flex-col gap-2 mb-8 md:mb-12 scroll-mt-28">
    <span className="text-indigo-600 font-black text-[10px] md:text-xs uppercase tracking-[0.4em]">{number}</span>
    <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight">{title}</h2>
    <div className="w-12 md:w-16 h-1.5 bg-indigo-600 rounded-full mt-2"></div>
  </div>
);

const ActionCard = ({ title, description, icon, onClick, accent }: { title: string, description: string, icon: React.ReactNode, onClick: () => void, accent: string }) => (
  <button 
    onClick={onClick}
    className="group flex flex-col p-6 md:p-8 bg-white rounded-[2rem] md:rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl hover:border-indigo-100 transition-all text-left relative overflow-hidden h-full"
  >
    <div className={`p-4 rounded-2xl mb-6 transition-colors duration-300 ${accent} group-hover:bg-indigo-600 group-hover:text-white inline-flex w-fit`}>
      {icon}
    </div>
    <h3 className="text-xl font-black text-slate-900 mb-3 tracking-tight">{title}</h3>
    <p className="text-slate-500 text-sm leading-relaxed font-medium mb-auto">{description}</p>
    <div className="mt-6 flex items-center gap-2 text-indigo-600 font-black text-[10px] uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
      Launch Task <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
    </div>
  </button>
);

const TrusteeCard = ({ name, role, phone, onCopy }: { name: string, role: string, phone: string, onCopy: (p: string) => void }) => (
  <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between group h-full">
    <div className="mb-6">
      <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest block mb-2">{role}</span>
      <h3 className="text-lg font-bold text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-1">{name}</h3>
      <p className="text-sm text-slate-400 font-semibold mt-1">{phone}</p>
    </div>
    <div className="flex gap-2">
      <a 
        href={`tel:${phone.replace(/\D/g, '')}`}
        className="flex-1 bg-slate-900 text-white text-[10px] font-black py-4 rounded-2xl text-center hover:bg-indigo-600 transition-all uppercase tracking-widest"
      >
        Call
      </a>
      <button 
        onClick={() => onCopy(phone)}
        className="px-4 bg-slate-50 text-slate-400 border border-slate-100 rounded-2xl hover:bg-slate-100 transition-all flex items-center justify-center"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
      </button>
    </div>
  </div>
);

const ActionIcon = ({ type }: { type?: string }) => {
  switch (type) {
    case 'meeting':
      return <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
    case 'organize':
      return <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>;
    case 'zoom':
      return <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>;
    default:
      return <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>;
  }
};

export default function App() {
  const [userName, setUserName] = useState('');
  const [isOptedIn, setIsOptedIn] = useState(true);
  const [showRecipients, setShowRecipients] = useState(false);
  const [toast, setToast] = useState({ message: '', visible: false });
  const [activeScript, setActiveScript] = useState<'short' | 'long'>('short');
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(0);

  const activeTemplate = EMAIL_TEMPLATES[selectedTemplateIndex];

  const showToast = (message: string) => {
    setToast({ message, visible: true });
    setTimeout(() => setToast({ message: '', visible: false }), 3000);
  };

  const replacePlaceholders = useCallback((text: string) => {
    return text
      .replace(/{DISTRICT_NAME}/g, DISTRICT_NAME)
      .replace(/{HUB_URL}/g, window.location.href)
      .replace(/{YOUR_NAME}/g, userName || "[Your Name]");
  }, [userName]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showToast(`Copied ${label}`);
  };

  const handleEmail = () => {
    const subject = replacePlaceholders(activeTemplate.subject);
    const body = replacePlaceholders(activeTemplate.body);
    const emailStrings = RECIPIENTS.map(r => r.email.trim());
    const to = emailStrings[0] || '';
    const cc = emailStrings.slice(1).join(';'); 
    const params = [];
    params.push(`subject=${encodeURIComponent(subject)}`);
    if (cc) params.push(`cc=${cc}`);
    if (isOptedIn && TRACKING_EMAIL) params.push(`bcc=${encodeURIComponent(TRACKING_EMAIL)}`);
    params.push(`body=${encodeURIComponent(body)}`);
    window.location.href = `mailto:${to}?${params.join('&')}`;
  };

  const handleCalendarInvite = () => {
    const { title, startISO, endISO, location, description } = NEXT_MEETING;
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//PVUSD Advocacy Hub//EN',
      'BEGIN:VEVENT',
      `SUMMARY:${title}`,
      `DTSTART:${startISO}`,
      `DTEND:${endISO}`,
      `LOCATION:${location}`,
      `DESCRIPTION:${description}`,
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'BEGIN:VALARM',
      'TRIGGER:-PT30M',
      'ACTION:DISPLAY',
      'DESCRIPTION:Reminder',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'pvusd-meeting-invite.ics');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("Calendar Invite Generated!");
  };

  const handleShareSMS = () => {
    const body = encodeURIComponent(SOCIAL_CONFIG.SMS_BODY + window.location.href);
    window.location.href = `sms:?body=${body}`;
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({ top: element.getBoundingClientRect().top + window.pageYOffset - 100, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toast message={toast.message} visible={toast.visible} />

      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
             <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-600 rounded-lg md:rounded-xl flex items-center justify-center text-white font-black text-lg md:text-xl shrink-0">{UI_CONFIG.NAV_LOGO_LETTER}</div>
             <span className="font-black text-slate-900 tracking-tighter text-sm md:text-base whitespace-nowrap">{UI_CONFIG.NAV_TITLE}</span>
          </div>
          <div className="hidden lg:flex items-center gap-8 text-[10px] font-black uppercase tracking-widest text-slate-400">
            <button onClick={() => scrollToSection('take-action')} className="hover:text-indigo-600 transition-colors">{UI_CONFIG.NAV_LINK_1}</button>
            <button onClick={() => scrollToSection('call-steps')} className="hover:text-indigo-600 transition-colors">{UI_CONFIG.NAV_LINK_2}</button>
            <button onClick={() => scrollToSection('next-actions')} className="hover:text-indigo-600 transition-colors">{UI_CONFIG.NAV_LINK_3}</button>
            <button onClick={() => scrollToSection('share')} className="hover:text-indigo-600 transition-colors">{UI_CONFIG.NAV_LINK_4}</button>
          </div>
          <button onClick={() => scrollToSection('take-action')} className="bg-slate-900 text-white px-4 md:px-6 py-2 md:py-3 rounded-xl md:rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-all">
            {UI_CONFIG.NAV_START_BUTTON}
          </button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-24 space-y-24 md:space-y-32">
        {/* Hero */}
        <section className="text-center space-y-6 md:space-y-8">
          <div className="inline-block px-4 py-1.5 bg-indigo-50 text-indigo-700 rounded-full text-[10px] font-black uppercase tracking-widest">
            {UI_CONFIG.HERO_TAG}
          </div>
          <h1 
            className="text-4xl sm:text-5xl md:text-8xl font-[1000] text-slate-900 tracking-tight leading-[1.1] md:leading-[0.95]"
            dangerouslySetInnerHTML={{ __html: UI_CONFIG.HERO_TITLE }}
          />
          <p className="text-base md:text-xl text-slate-500 max-w-2xl mx-auto font-semibold leading-relaxed px-2">
            {UI_CONFIG.HERO_DESCRIPTION}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 md:pt-8">
            <button onClick={() => scrollToSection('take-action')} className="bg-indigo-600 text-white px-8 md:px-10 py-4 md:py-5 rounded-2xl md:rounded-[2rem] font-black text-base md:text-lg hover:scale-105 transition-transform shadow-xl shadow-indigo-100">
              {UI_CONFIG.HERO_CTA_PRIMARY}
            </button>
            <button onClick={() => scrollToSection('call-steps')} className="bg-white border border-slate-200 text-slate-700 px-8 md:px-10 py-4 md:py-5 rounded-2xl md:rounded-[2rem] font-black text-base md:text-lg hover:bg-slate-50">
              {UI_CONFIG.HERO_CTA_SECONDARY}
            </button>
          </div>
        </section>

        {/* Identity Step */}
        <section className="bg-slate-900 rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-20 text-white shadow-2xl grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center overflow-hidden relative">
          <div className="space-y-4 md:space-y-6 relative z-10 text-center lg:text-left">
            <span className="text-indigo-400 font-black text-[10px] md:text-xs uppercase tracking-widest">{UI_CONFIG.PERSONALIZE_TAG}</span>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">{UI_CONFIG.PERSONALIZE_TITLE}</h2>
            <p className="text-slate-400 text-base md:text-lg font-medium leading-relaxed">{UI_CONFIG.PERSONALIZE_DESCRIPTION}</p>
          </div>
          <div className="bg-white/5 backdrop-blur-xl rounded-[2rem] md:rounded-[2.5rem] p-6 md:p-10 border border-white/10 space-y-6 md:space-y-8 relative z-10">
            <div className="space-y-3 md:space-y-4">
              <label className="text-[10px] font-black uppercase tracking-widest text-indigo-300">{UI_CONFIG.NAME_INPUT_LABEL}</label>
              <input 
                type="text" 
                placeholder={UI_CONFIG.NAME_INPUT_PLACEHOLDER}
                className="w-full bg-slate-800/50 px-6 md:px-8 py-4 md:py-6 rounded-xl md:rounded-2xl border border-white/10 focus:ring-4 focus:ring-indigo-500/20 text-lg md:text-xl font-bold transition-all placeholder:text-slate-600 outline-none"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between gap-4 p-4 md:p-6 bg-indigo-600/10 rounded-xl md:rounded-2xl border border-indigo-500/20">
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm md:text-base line-clamp-1">{UI_CONFIG.JOIN_UPDATES_TITLE}</p>
                <p className="text-[9px] md:text-[10px] text-indigo-300/60 font-bold uppercase mt-1 line-clamp-1">{UI_CONFIG.JOIN_UPDATES_SUBTEXT}</p>
              </div>
              <button 
                onClick={() => setIsOptedIn(!isOptedIn)}
                className={`w-14 h-8 shrink-0 rounded-full transition-all relative outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 focus:ring-offset-slate-900 ${isOptedIn ? 'bg-indigo-500' : 'bg-slate-700'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-lg transition-all ${isOptedIn ? 'left-7' : 'left-1'}`} />
              </button>
            </div>
          </div>
        </section>

        {/* Step 1: Outreach */}
        <section className="space-y-8 md:space-y-12">
          <SectionHeader id="take-action" number="01" title={UI_CONFIG.SECTION_1_TITLE} />
          
          <div className="space-y-6 md:space-y-8">
            <div className="flex flex-wrap gap-2 md:gap-4 justify-center md:justify-start">
              {EMAIL_TEMPLATES.map((t, i) => (
                <button 
                  key={t.id} 
                  onClick={() => setSelectedTemplateIndex(i)}
                  className={`px-4 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl font-black text-[9px] md:text-xs uppercase tracking-widest border transition-all ${selectedTemplateIndex === i ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg' : 'bg-white border-slate-100 text-slate-500 hover:border-indigo-200'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="bg-white rounded-[2rem] md:rounded-[3rem] border border-slate-100 shadow-xl overflow-hidden">
               <div className="p-6 md:p-16 space-y-8 md:space-y-10">
                  <div className="flex flex-col gap-4">
                    <button 
                      onClick={() => setShowRecipients(!showRecipients)}
                      className="flex items-center gap-2 text-indigo-600 font-black text-[10px] uppercase tracking-widest hover:text-indigo-700 transition-colors self-start"
                    >
                      {showRecipients ? 'Hide Recipients' : 'View Recipients'}
                      <svg className={`w-3 h-3 transition-transform ${showRecipients ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    
                    {showRecipients && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4 md:p-6 bg-slate-50 rounded-2xl border border-slate-100">
                        {RECIPIENTS.map((r, i) => (
                          <div key={i} className="flex flex-col min-w-0">
                            <span className="text-[10px] md:text-[11px] font-black text-slate-900 truncate">{r.name}</span>
                            <span className="text-[8px] md:text-[9px] font-bold text-slate-400 uppercase tracking-tighter truncate">{r.role}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="bg-slate-50 p-6 md:p-10 rounded-[1.5rem] md:rounded-[2.5rem] border border-slate-100 relative">
                     <div className="hidden md:block absolute top-8 right-8 text-[10px] font-black text-indigo-600 uppercase tracking-widest">Draft Preview</div>
                     <h4 className="text-xl md:text-2xl font-black text-slate-900 mb-4 md:mb-6">{replacePlaceholders(activeTemplate.subject)}</h4>
                     <p className="text-slate-600 leading-relaxed font-semibold text-sm md:text-lg whitespace-pre-wrap">{replacePlaceholders(activeTemplate.body)}</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     <button onClick={handleEmail} className="bg-indigo-600 text-white py-4 md:py-6 px-6 md:px-10 rounded-xl md:rounded-2xl font-black text-[10px] md:text-xs uppercase tracking-widest hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 order-1 sm:order-none">
                        {UI_CONFIG.BUTTON_SEND_EMAIL}
                        <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                     </button>
                     <button onClick={() => handleCopy(replacePlaceholders(activeTemplate.body), 'Email Body')} className="bg-slate-900 text-white py-4 md:py-6 px-6 md:px-10 rounded-xl md:rounded-2xl font-black text-[10px] md:text-xs uppercase tracking-widest hover:bg-black transition-all order-2 sm:order-none">
                        {UI_CONFIG.BUTTON_COPY_CONTENT}
                     </button>
                  </div>
               </div>
            </div>
          </div>
        </section>

        {/* Step 2: Call */}
        <section className="space-y-8 md:space-y-12">
          <SectionHeader id="call-steps" number="02" title={UI_CONFIG.SECTION_2_TITLE} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {BOARD_MEMBERS.map((m, i) => <TrusteeCard key={i} {...m} onCopy={(p) => handleCopy(p, `${m.name}'s Number`)} />)}
          </div>
          <div className="bg-slate-900 rounded-[2rem] md:rounded-[3rem] p-8 md:p-16 text-white space-y-8 md:space-y-10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-b border-white/10 pb-6 md:pb-8">
              <h3 className="text-xl md:text-2xl font-black tracking-tight">Interactive Script</h3>
              <div className="flex gap-2 p-1 bg-white/5 rounded-xl border border-white/10 w-fit">
                <button onClick={() => setActiveScript('short')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${activeScript === 'short' ? 'bg-indigo-600 shadow-md' : 'text-slate-500 hover:text-slate-300'}`}>Short</button>
                <button onClick={() => setActiveScript('long')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${activeScript === 'long' ? 'bg-indigo-600 shadow-md' : 'text-slate-500 hover:text-slate-300'}`}>Full</button>
              </div>
            </div>
            <p className="text-lg md:text-2xl font-bold text-indigo-100 italic leading-relaxed">"{replacePlaceholders(CALL_SCRIPTS[activeScript])}"</p>
          </div>
        </section>

        {/* Step 3: Plan */}
        <section className="space-y-8 md:space-y-12">
          <SectionHeader id="next-actions" number="03" title={UI_CONFIG.SECTION_3_TITLE} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
            {ACTION_CARDS.map((card) => (
              <ActionCard 
                key={card.id}
                accent={card.accent}
                title={card.title}
                description={card.description}
                icon={<ActionIcon type={card.type} />}
                onClick={() => {
                  if (card.actionType === 'calendar') {
                    handleCalendarInvite();
                  } else if (card.url) {
                    window.open(card.url, '_blank');
                  } else if (card.scrollToId) {
                    scrollToSection(card.scrollToId);
                  } else if (card.toastMessage) {
                    showToast(card.toastMessage);
                  }
                }}
              />
            ))}
          </div>
        </section>

        {/* Share Section */}
        <section id="share" className="bg-indigo-600 rounded-[2.5rem] md:rounded-[4rem] p-8 md:p-24 text-white space-y-12 md:space-y-16 shadow-2xl shadow-indigo-200">
           <div className="text-center space-y-4 md:space-y-6">
              <h2 className="text-3xl md:text-6xl font-black tracking-tight leading-tight">{UI_CONFIG.SECTION_4_TITLE}</h2>
              <p className="text-base md:text-xl font-bold opacity-80 max-w-2xl mx-auto px-2">One share represents dozens of unheard voices. Amplify the campaign today.</p>
           </div>

           <div className="max-w-xl mx-auto bg-white/10 backdrop-blur-xl rounded-[2rem] p-6 md:p-8 border border-white/20 text-center space-y-4">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-200">Official Movement Account</p>
              <a 
                href={SOCIAL_CONFIG.INSTAGRAM_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-2xl md:text-5xl font-black tracking-tighter hover:text-indigo-200 transition-colors block"
              >
                {SOCIAL_CONFIG.INSTAGRAM_HANDLE}
              </a>
              <div className="pt-2">
                <a 
                  href={SOCIAL_CONFIG.INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-indigo-600 px-6 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest hover:scale-105 transition-transform inline-block"
                >
                  Follow Us
                </a>
              </div>
           </div>

           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <button 
                onClick={() => window.open(`${SOCIAL_CONFIG.X_URL}${encodeURIComponent(SOCIAL_CONFIG.SMS_BODY)}&url=${encodeURIComponent(window.location.href)}`, '_blank')}
                className="bg-slate-900 text-white p-6 md:p-8 rounded-[1.5rem] md:rounded-[2rem] font-black flex flex-col items-center gap-4 hover:bg-black transition-all group"
              >
                 <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                 <span className="text-[10px] uppercase tracking-widest">Post to X</span>
              </button>

              <button 
                onClick={() => window.open(`${SOCIAL_CONFIG.FACEBOOK_URL}${encodeURIComponent(window.location.href)}`, '_blank')}
                className="bg-white text-indigo-600 p-6 md:p-8 rounded-[1.5rem] md:rounded-[2rem] font-black flex flex-col items-center gap-4 hover:bg-slate-50 transition-all group"
              >
                 <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                 <span className="text-[10px] uppercase tracking-widest text-slate-900">Facebook</span>
              </button>

              <button 
                onClick={() => handleCopy(replacePlaceholders(IG_CAPTION_TEMPLATE), 'Caption')}
                className="bg-indigo-500 text-white p-6 md:p-8 rounded-[1.5rem] md:rounded-[2rem] font-black flex flex-col items-center gap-4 hover:bg-indigo-400 transition-all group"
              >
                 <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                 <span className="text-[10px] uppercase tracking-widest whitespace-nowrap">Instagram Caption</span>
              </button>

              <button 
                onClick={handleShareSMS}
                className="bg-slate-900 text-white p-6 md:p-8 rounded-[1.5rem] md:rounded-[2rem] font-black flex flex-col items-center gap-4 hover:bg-slate-800 transition-all group"
              >
                 <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                 <span className="text-[10px] uppercase tracking-widest">Text a Friend</span>
              </button>
           </div>
        </section>
      </main>

      <footer className="bg-slate-900 text-white py-16 md:py-24 text-center">
        <div className="max-w-4xl mx-auto px-6 space-y-12">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-indigo-600 rounded-[1.25rem] md:rounded-[1.5rem] flex items-center justify-center text-white font-black text-2xl md:text-3xl mx-auto mb-8 shadow-2xl">P</div>
          <p className="text-slate-400 font-bold max-w-lg mx-auto leading-relaxed text-sm md:text-base">{UI_CONFIG.FOOTER_DESCRIPTION}</p>
          <div className="pt-12 border-t border-white/5 space-y-4">
             <p className="text-slate-600 text-[9px] md:text-[10px] font-black uppercase tracking-[0.3em] px-4">{UI_CONFIG.LEGAL_DISCLAIMER}</p>
             <p className="text-slate-700 text-[8px] md:text-[9px] font-black uppercase tracking-widest">&copy; 2024 Community Schools Advocacy Collective</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
